import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { PartyPopper, Trophy } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { FLYER_URL, IG_URL, STORY_URL, TRACK_URL } from "./flyerConstants";

import { API } from "./api";

const FUNDING_SNAPSHOT = [
  "Access $1,000 – $500,000+ for your business",
  "Low interest rates starting at only 1.00% per month",
  "Flexible, revolving credit lines for expansion, emergencies, or other business needs",
  "Up to 36-month financing terms",
  "No credit score minimum to apply",
  "Businesses in operation for 6+ months may be eligible",
  "Commercial Real Estate Funding available",
];

const READINESS_CHECKLIST = [
  "Have your business registration documents ready",
  "Keep recent business bank statements handy",
  "Know your average monthly revenue",
  "Decide how much funding you need and what it is for",
  "Set aside 15 minutes to complete the application",
  "Start through the referral link so your application is tracked",
];

const MILESTONES = [
  { min: 5, label: "Funding Starter" },
  { min: 10, label: "Growth Champion" },
  { min: 25, label: "Legacy Builder" },
];

export default function MembersPage() {
  const { user } = useAuth();
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/referrals/me`, { withCredentials: true })
      .then(({ data }) => setReferral(data))
      .catch(() => setReferral(null));
  }, [user]);

  const copyLink = async () => {
    if (!referral) return;
    try {
      await navigator.clipboard.writeText(referral.link);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = referral.link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (user === null) {
    return (
      <div data-testid="members-loading" className="min-h-[60vh] flex items-center justify-center text-blue-200/70">
        Checking your session…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const count = referral ? referral.count : null;
  const reached = count === null ? [] : MILESTONES.filter((m) => count >= m.min);
  const current = reached.length ? reached[reached.length - 1] : null;
  const next = MILESTONES.find((m) => count < m.min) || null;

  return (
    <div data-testid="members-page" className="flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md">
        <h1 data-testid="members-welcome" className="text-2xl font-bold text-white">
          Welcome back, {user.name || user.email}
        </h1>
        <p className="mt-2 text-sm text-blue-200/70">Members-only funding resources.</p>

        {current && (
          <div
            data-testid="milestone-banner"
            className="mt-6 rounded-2xl p-6 bg-gradient-to-r from-[#ffcd3c] to-[#f3ad00] text-[#0a1a3f] shadow-2xl shadow-[#ffcd3c]/20 relative overflow-hidden"
          >
            <PartyPopper className="absolute right-4 top-4 h-16 w-16 opacity-20" />
            <div className="flex items-center gap-3">
              <Trophy className="h-9 w-9 shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wide opacity-80">Milestone reached</p>
                <p data-testid="milestone-label" className="text-xl font-bold leading-tight">
                  {current.label} — {count} referrals!
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm font-medium opacity-90">
              Screenshot this banner and share your win with your community!
            </p>
          </div>
        )}

        <div
          data-testid="referral-card"
          className="mt-6 bg-[#0a1c46] border border-[#ffcd3c]/40 rounded-2xl p-6"
        >
          <h2 className="text-[#ffcd3c] font-bold text-lg">Your referral tracker</h2>
          <div className="mt-4 flex items-end gap-3">
            <span data-testid="referral-count" className="text-5xl font-bold text-white leading-none">
              {count === null ? "…" : count}
            </span>
            <span className="text-sm text-blue-200/70 pb-1">
              {count === 0 ? "people you've sent to sign up — share your link!" : "people you've sent to sign up"}
            </span>
          </div>
          {referral && (
            <p data-testid="referral-weekly" className="mt-1 text-xs text-blue-200/70">
              {referral.weekly_count ?? 0} this week
            </p>
          )}
          {next ? (
            <p data-testid="milestone-progress" className="mt-2 text-xs text-blue-200/70">
              {next.min - count} more {next.min - count === 1 ? "referral" : "referrals"} to reach {next.label} ({next.min})
            </p>
          ) : count !== null ? (
            <p data-testid="milestone-top" className="mt-2 text-xs font-bold text-[#ffcd3c]">
              Top milestone reached — legacy in the making!
            </p>
          ) : null}
          <div
            data-testid="referral-link"
            className="mt-4 rounded-lg bg-[#04102e] border border-white/15 px-3 py-2.5 text-xs text-blue-100/80 break-all"
          >
            {referral ? referral.link : "Loading your link…"}
          </div>
          <button
            data-testid="copy-referral-btn"
            onClick={copyLink}
            disabled={!referral}
            className="mt-4 w-full rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-2.5 hover:brightness-110 transition disabled:opacity-50"
          >
            {copied ? "Copied!" : "Copy My Referral Link"}
          </button>
        </div>

        <div
          data-testid="funding-snapshot-card"
          className="mt-4 bg-[#0a1c46] border border-white/10 rounded-2xl p-6"
        >
          <h2 className="text-[#ffcd3c] font-bold text-lg">Funding snapshot</h2>
          <ul className="mt-4 space-y-2">
            {FUNDING_SNAPSHOT.map((item) => (
              <li key={item} className="flex gap-2 text-sm text-blue-100/90">
                <span className="text-[#ffcd3c]">✓</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-blue-200/50">Straight from the flyer — share it with anyone who asks.</p>
        </div>

        <div
          data-testid="readiness-card"
          className="mt-4 bg-[#0a1c46] border border-white/10 rounded-2xl p-6"
        >
          <h2 className="text-[#ffcd3c] font-bold text-lg">Application readiness checklist</h2>
          <ul className="mt-4 space-y-2">
            {READINESS_CHECKLIST.map((item) => (
              <li key={item} className="flex gap-2 text-sm text-blue-100/90">
                <span className="text-[#ffcd3c]">□</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <a
          data-testid="members-cta-btn"
          href={referral ? referral.link : TRACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 block w-full text-center rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 hover:brightness-110 transition"
        >
          Start Your Funding Application
        </a>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full">
          <a
            data-testid="members-flyer-link"
            href={FLYER_URL}
            download="HOPE-CDS-flyer-signup-link.png"
            className="flex-1 text-center text-sm rounded-full border border-white/20 text-blue-100 py-2.5 px-4 hover:border-[#ffcd3c] hover:text-[#ffcd3c] transition"
          >
            Flyer
          </a>
          <a
            data-testid="members-ig-link"
            href={IG_URL}
            download="HOPE-CDS-flyer-1080x1350.jpg"
            className="flex-1 text-center text-sm rounded-full border border-white/20 text-blue-100 py-2.5 px-4 hover:border-[#ffcd3c] hover:text-[#ffcd3c] transition"
          >
            IG Post
          </a>
          <a
            data-testid="members-story-link"
            href={STORY_URL}
            download="HOPE-CDS-flyer-1080x1920.jpg"
            className="flex-1 text-center text-sm rounded-full border border-white/20 text-blue-100 py-2.5 px-4 hover:border-[#ffcd3c] hover:text-[#ffcd3c] transition"
          >
            Story
          </a>
        </div>

        <p className="mt-6 text-xs text-blue-200/50 text-center">
          Want the public view? <Link to="/" data-testid="members-home-link" className="text-[#ffcd3c]">Back to the flyer</Link>
        </p>
      </div>
    </div>
  );
}
